'use client'

import { type ReactNode } from 'react'

import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { LoadingIndicator } from '~/components/LoadingIndicator'
import { Message } from '~/components/component-library/Message'
import {
  RegistryChain,
  initChainRegistry,
  isChainRegistryInitialized,
} from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { captureException } from '~/utils/errors'
import { getAxiosConfig } from '~/utils/requests'

const CHAINS_PATH = 'api/metadata/chains'

const fetchChainRegistry = async (): Promise<{ chains: RegistryChain[] }> => {
  const config = getAxiosConfig()
  const response = await axios.get<{ chains: RegistryChain[] }>(
    `${config.baseURL}/${CHAINS_PATH}`,
    config,
  )
  // A malformed 200 (proxy error page, envelope drift) must hit the error/retry
  // UI below — seeding an empty registry would render the app and crash it.
  // Captured in Sentry because it blocks boot for everyone and only the client
  // can see it: the server observes a successful 200. Plain network errors and
  // 500s stay uncaptured — client connectivity is noise and the server's own
  // monitoring covers endpoint failures.
  if (
    !Array.isArray(response.data?.chains) ||
    response.data.chains.length === 0
  ) {
    const error = new Error('Malformed chain registry response')
    captureException(error)
    throw error
  }
  return response.data
}

/**
 * Nothing below this can render: every chain-aware provider, hook and component
 * reads the registry synchronously, so the app boots only once it is seeded.
 */
export const ChainRegistryGate = ({ children }: { children: ReactNode }) => {
  const { data, isError, refetch, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.CHAIN_REGISTRY],
    queryFn: fetchChainRegistry,
    staleTime: 300_000, // 5 minutes, matches the endpoint's Cache-Control
    retry: 3,
  })

  if (data) initChainRegistry(data.chains)

  if (isChainRegistryInitialized()) return <>{children}</>

  if (isError)
    return (
      <div className="flex h-dvh w-full items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Message
            variant="danger"
            title="Couldn't load networks"
            action={{
              label: 'Retry',
              isLoading: isFetching,
              onClick: () => refetch(),
            }}
          >
            Splits couldn&apos;t reach the network list. Check your connection
            and try again.
          </Message>
        </div>
      </div>
    )

  return <LoadingIndicator />
}
